import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { subscribeToPush } from "./NotificationBell";
import { isNativeSync } from "../native/pushNative";
import { isTunnelPath } from "../utils/tunnelPaths";

// Pré-prompt CONTEXTUEL des notifications (soft prompt) + guidage de RÉCUPÉRATION
// pour les utilisateurs déjà en "denied". Décision archi RC + reco recherche
// (Braze/OneSignal/web.dev/android-browser-helper) :
//   - On ne déclenche JAMAIS le prompt natif au chargement (refus quasi
//     irréversible sur Android 13/Chrome ; après 2 refus = USER_FIXED, plus
//     aucun re-prompt possible — seuls les Réglages débloquent).
//   - mode "prompt" (permission "default") : bandeau non bloquant ; « Activer »
//     appelle le vrai Notification.requestPermission() dans un geste utilisateur
//     (relayé au prompt natif POST_NOTIFICATIONS par la délégation TWA).
//   - mode "guide" (permission "denied") : on ne peut PLUS prompter → on guide
//     vers Réglages Android (impossible d'ouvrir les réglages depuis le web seul).
//     Affiché UNE seule fois (dismiss long) pour ne pas harceler.
const DISMISS_KEY = "push_primer_dismissed_until";          // mode prompt
const DISMISS_DAYS = 7;
const DISMISS_KEY_DENIED = "push_primer_denied_dismissed";  // mode guide (one-shot)

const isSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

export default function PushPermissionPrimer() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [mode, setMode] = useState(null); // "prompt" | "guide" | null
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // App native (Capacitor) : la permission est gérée nativement au boot
    // (pushNative), pas par ce pré-prompt web → on ne l'affiche jamais.
    if (isNativeSync()) { setMode(null); return undefined; }
    if (!user || !isSupported()) { setMode(null); return undefined; }
    const perm = Notification.permission;

    if (perm === "granted") { setMode(null); return undefined; }

    if (perm === "denied") {
      // Guidage one-shot : si déjà affiché/écarté une fois, on n'insiste plus.
      try {
        if (localStorage.getItem(DISMISS_KEY_DENIED) === "1") { setMode(null); return undefined; }
      } catch (e) { /* localStorage indispo */ }
      const t = setTimeout(() => setMode("guide"), 4000);
      return () => clearTimeout(t);
    }

    // perm === "default" → pré-prompt, sauf si reporté récemment.
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) { setMode(null); return undefined; }
    } catch (e) { /* localStorage indispo : on affichera quand même */ }
    const t = setTimeout(() => setMode("prompt"), 3500);
    return () => clearTimeout(t);
  }, [user]);

  const remindLater = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DAYS * 86400000)); } catch (e) { /* noop */ }
    setMode(null);
  };

  const dismissGuide = () => {
    try { localStorage.setItem(DISMISS_KEY_DENIED, "1"); } catch (e) { /* noop */ }
    setMode(null);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const ok = await subscribeToPush(true); // geste utilisateur → prompt natif
      if (ok) {
        toast.success("Notifications activées ! Vous serez prévenu des messages, des ventes et des offres reçues. Les bons plans restent désactivés (Paramètres → Notifications).");
        setMode(null);
      } else if (Notification.permission === "denied") {
        // Le prompt a été refusé à l'instant → bascule en guidage.
        setMode("guide");
      } else {
        remindLater(); // prompt fermé sans choix → on retentera plus tard
      }
    } catch (e) {
      remindLater();
    } finally {
      setBusy(false);
    }
  };

  // 06/10/2026 — JAMAIS DANS UN TUNNEL (audit des tunnels d'achat, parcours
  // réel au navigateur) : le bandeau s'ouvrait par-dessus l'étape
  // « Vérification » et masquait la saisie du code de rencontre, puis le
  // récapitulatif. Même liste que la bannière PWA et le bandeau cookies
  // (`utils/tunnelPaths`, source unique). Il réapparaît hors tunnel.
  if (!mode || isTunnelPath(pathname)) return null;

  const isGuide = mode === "guide";

  return (
    <div
      className="fixed inset-x-0 z-[60] px-3"
      style={{ bottom: "calc(72px + env(safe-area-inset-bottom, 0px))" }}
      role="dialog"
      aria-label={isGuide ? "Réactiver les notifications" : "Activer les notifications"}
    >
      <div className="mx-auto max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 flex items-start gap-3">
        <span className="text-2xl flex-shrink-0" aria-hidden="true">{isGuide ? "🔕" : "🔔"}</span>
        <div className="min-w-0 flex-1">
          {isGuide ? (
            <>
              <p className="font-bold text-slate-900 text-[15px] leading-snug">Notifications désactivées</p>
              <p className="text-slate-500 text-[13px] mt-0.5 leading-snug">
                Pour être prévenu des messages et de vos ventes, réactivez-les dans&nbsp;:
                <strong>{/iPhone|iPad|iPod/i.test(navigator.userAgent)
                  ? " Réglages → Notifications → Gively → Autoriser."
                  : " Réglages → Applications → Gively → Notifications → Autoriser."}</strong>
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={dismissGuide}
                  className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors"
                >
                  J'ai compris
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="font-bold text-slate-900 text-[15px] leading-snug">Activer les notifications</p>
              <p className="text-slate-500 text-[13px] mt-0.5 leading-snug">
                Soyez prévenu dès qu'on vous envoie un message, qu'un article se vend ou qu'on vous fait une offre.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="button"
                  onClick={enable}
                  disabled={busy}
                  className="px-4 py-2 rounded-xl bg-[#1DA1F2] text-white text-sm font-semibold hover:bg-[#1791db] transition-colors disabled:opacity-50"
                >
                  {busy ? "…" : "Activer"}
                </button>
                <button
                  type="button"
                  onClick={remindLater}
                  className="px-3 py-2 rounded-xl text-slate-500 text-sm font-medium hover:bg-slate-100 transition-colors"
                >
                  Plus tard
                </button>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={isGuide ? dismissGuide : remindLater}
          aria-label="Fermer"
          className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
